import { FC } from 'react';
import Head from 'next/head';
import { useMediaQuery } from 'react-responsive';
import { defineMessages, useIntl } from 'react-intl';
import { Page } from 'src/components';
import theme from 'src/utils/theme';
import * as S from './styles';

const msgs = defineMessages({
  loading: { id: 'blob_session_loading', defaultMessage: 'Loading session…' },
});

const PROGRESS_SEGMENTS = [
  'seg-a',
  'seg-b',
  'seg-c',
  'seg-d',
  'seg-e',
  'seg-f',
  'seg-g',
  'seg-h',
] as const;

export const LoadingSkeleton: FC<{ title?: string }> = ({ title }) => {
  const intl = useIntl();
  const isMobile = useMediaQuery({ maxWidth: theme.sz.md });
  const loadingLabel = intl.formatMessage(msgs.loading);

  return (
    <Page footerStyle="none">
      {title && (
        <Head>
          <title>{title} - Memrise</title>
        </Head>
      )}
      {isMobile ? (
        <S.MobileStage
          $hasMedia
          data-testid="blob-session-skeleton"
          aria-busy="true"
          aria-label={loadingLabel}
        >
          <S.MobileMediaBone aria-hidden />
          <S.MobileTopBar aria-hidden>
            <S.MobileTopRow>
              <S.MobileBackBone />
              <S.MobileStats>
                <S.MobileStatBone />
                <S.MobileStatBone />
              </S.MobileStats>
            </S.MobileTopRow>
            <S.ProgressTrack>
              {PROGRESS_SEGMENTS.map(id => (
                <S.ProgressSegment key={`mobile-${id}`} />
              ))}
            </S.ProgressTrack>
          </S.MobileTopBar>
          <S.MobileOverlay>
            <S.MobileCardBody $railBeside={false}>
              <S.MobileContent>
                <S.CardContent aria-hidden>
                  <S.MobilePhraseBone />
                  <S.MobileDefinitionBone />
                  <S.MobileButtonBone />
                </S.CardContent>
              </S.MobileContent>
            </S.MobileCardBody>
          </S.MobileOverlay>
        </S.MobileStage>
      ) : (
        <S.DesktopStage
          data-testid="blob-session-skeleton"
          aria-busy="true"
          aria-label={loadingLabel}
        >
          <S.HeaderBar aria-hidden>
            <S.LogoBone />
            <S.HeaderActions>
              <S.HeaderPill />
              <S.HeaderPill />
              <S.HeaderPill />
            </S.HeaderActions>
          </S.HeaderBar>
          <S.ProgressRow aria-hidden>
            <S.ProgressRowInner>
              <S.ProgressTrack>
                {PROGRESS_SEGMENTS.map(id => (
                  <S.ProgressSegment key={`desktop-${id}`} />
                ))}
              </S.ProgressTrack>
              <S.ExitBone />
            </S.ProgressRowInner>
          </S.ProgressRow>
          <S.SessionLayout>
            <S.MediaColumn>
              <S.MediaBone aria-hidden />
            </S.MediaColumn>
            <S.ContentColumn>
              <S.CardArea>
                <S.CardContent aria-hidden>
                  <S.PhraseBone />
                  <S.DefinitionBone />
                  <S.ButtonBone />
                </S.CardContent>
              </S.CardArea>
            </S.ContentColumn>
          </S.SessionLayout>
        </S.DesktopStage>
      )}
    </Page>
  );
};
